import stream from 'app/native/node/stream';
import {
  FOCUS,
  EVENT_GENERATOR_WARNING,
  EVENT_GENERATOR_STATUS,
  EVENT_SW_VER,
  OPERATING_MODE,
  EVENT_GENERATOR_RAD_PARAMETERS_CHANGE,
  EVENT_GENERATOR_ERROR,
  EVENT_GENERATOR_PREP_STATE,
  EVENT_GENERATOR_EXPOSURE_STATE,
  EVENT_GENERATOR_RAD_PARAMETERS_EXPOSURE,
  EVENT_GENERATOR_SWITCH,
  EVENT_GENERATOR_THICKNESS,
} from 'app/xray/generator/constants';
import TimeoutPromise from 'app/utils/TimeoutPromise';
import SequentialModbusRTU from 'app/adapters/SequentialModbusRTU';
import computeClosestCompatibleGeneratorConstants from 'app/utils/computeClosestCompatibleGeneratorConstants';
import _ from 'lodash';
import { convertErrorCodeToExamionCode } from './xrayGeneratorErrorUtils';

const SOFTWARE_LOCK_OK = 0x5362;
const AUTORISE_LOG_KEY = 0x10cc;
// Minimal local typings to avoid implicit any

enum ExceptionStatusOffset {
  PREP_TIR = 1 << 0, // Tir asked for once prep confirmed
  ERROR = 1 << 1, // An error is present | Must be 0 for prep
  WAITING_FOR_CONSTANTS = 1 << 2, // Constants must be set | Must be 0 for prep
  ERROR_SYSTEM_LOCKED = 1 << 3, // Too many hard fault system must be reset by technician | Must be 0 for prep
  COMMUNICATION_DEFAULT = 1 << 4, // Not clear for me | Must be 0 for prep
  WAIT_CAPACITOR = 1 << 5, // Attente de la charge des condensateurs | Must be 0 for prep
  BIP_READY = 1 << 6, // BIP has been heard from prep | Must be 1 for XRay shot
  NEED_WAIT_TUBE = 1 << 7, // Tube must be ready | Must be 0 for prep
}

enum OperatingMode {
  SLEEPING = 1,
  STANDBY = 2,
  SHUTDOWN = 121,
}

interface RadParameters {
  kV: number;
  mA: number;
  ms: number;
  focus?: number; // FOCUS.* values are numeric
}

enum GeneratorUnitStatus {
  GENERATOR_NOT_DETECTED = 0,
  SLEEPING_MODE,
  STANDBY_MODE,
  FIRST_TIME,
  XRAY_IN_PROGRESS,
  END_OF_XRAY,
  SYSTEM_ERROR,
  WAIT_CAPACITOR,
  SHUTDOWN_IN_PROGRESS = 16,
  WAIT_FIRST_COM = 16,
  INTERNAL_BOARD_COMMUNICATION = 32,
}

function getEnumKeyByValue<T extends object>(enumObject: T, value: T[keyof T]): string | undefined {
  return Object.keys(enumObject).find((key) => enumObject[key as keyof T] === value);
}

enum GeneratorReadyStatus {
  READY = 1,
  BUSY = 2,
}

enum MODBUS_ADDRESS {
  ADRESSE_MODBUS_RESULTAT_TIR = 0x10,
  ADRESSE_MODBUS_PARAM_TIR = 0x18,
  ADRESSE_MODBUS_VERIF_DEVEROUILLE_LOG = 0x1f,
  ADRESSE_MODBUS_RAZ_DEFAUT = 0x21,
  ADRESSE_MODBUS_TIMER_TUBE = 0x28,
  ADRESSE_MODBUS_ID_LOGICIEL = 0x29,
  ADRESSE_MODBUS_DEVEROUILLE_LOG = 0x29,
  ADRESSE_MODBUS_PHASE = 0x2d,
  ADRESSE_MODBUS_EPAISSEUR = 0x40,
  ADRESSE_MODBUS_VERSION = 0x4b,
  ADRESSE_MODBUS_QUEL_APPAREIL = 0x50,
  ADRESSE_MODBUS_ETAT_SWITCH = 0x53,
  ADRESSE_MODBUS_PEDALE_STATE = 0x53,
  ADRESSE_MODBUS_PREP_CONFIRM = 0x56,
  ADRESSE_MODBUS_RAD_ALLOWED = 0x57,
  ADRESSE_MODBUS_SHUTDOWN = 0x208,
  ADRESSE_MODBUS_TYPE_FOYER = 0x228,
  ADRESSE_MODBUS_BIT_CLEAR_ERROR = 0x3b8,
  ADRESSE_MODBUS_LISTE_ERROR = 0x1000,
  ADRESSE_MODBUS_VERIF_BLOCAGE = 0x1000,
  ADRESSE_MODBUS_ID_LOGICIEL_AUTORISE = 0x107d,
}
export enum ENUM_DEFAUT_VTECH { //ENUMERE LES NUMEROS DE TOUS LES DÉFAUTS POSSIBLES
  DEFAUT_STATOR_ALIMENTE_SANS_ORDRE = 1, //PROVOQUE 1 AUTOSHUTDOWN		V2.400
  DEFAUT_TIR_INTERDIT,
  ICF_INCORRECT_STBY = 3,
  ICF_TROP_FAIBLE_TIR,
  DEPASSEMENT_KV,
  DISSYMETRIE,
  DEPASSE_TEMP_TUBE,
  DEFAUT_CHARGE_CAPA,
  DEFAUT_IDENTICATION_LOGICIEL_PC, //V2.300
  EXPO_TROP_LONG = 12,
  DEFAUT_INTER_TIR,
  ATTENTE_CHARGE_CONDO,
  ANNULATION_TIR,
  MONTEE_KV_TROP_LENT,
  ECART_MA_20,
  ECART_MA_50,
  TMPS_PREPA,
  PAS_DONNEE_TIR = 21,
  MONTEE_KV_TROP_RAPIDE,
  IA_TROP_FORT,
  COURANT_STATOR_TROP_FAIBLE, //	V2.400
  COURANT_STATOR_TROP_FORT, //	V2.400
  COMM_AVEC_SECU = 26,
  COMM_AVEC_MEM,
  PAS_DE_KV,
  ICF_TROP_FORT_TIR,
  WATCHDOG_SECU,
  ERREUR_CONNECTIQUE,
  WATCHDOG_COMMANDE,

  ERREUR_COMMUNICATION_GENERATEUR = 0xcf,
}

function convertResultTirToStruct(resultTir: number[]) {
  const dose = 0;
  const kV = (resultTir[2] + 4) / 10;
  const mAs = ((resultTir[5] << 16) + resultTir[4]) / 1000;
  const mA = (resultTir[3] + 4) / 10;
  const ms = resultTir[0];
  const focus = mA < 100 ? FOCUS.SMALL : FOCUS.BIG;
  return {
    dose,
    kV,
    mAs,
    mA,
    ms,
    focus,
    status: resultTir[6],
    error: resultTir[7],
  };
}

function convertRadParamsToStruct(radParams: number[]) {
  const [unused, kVSet, mASet, msSet] = radParams;

  const radParameters = {
    kV: kVSet,
    mAs: (mASet * msSet) / 1000,
    mA: mASet,
    ms: msSet,
    focus: mASet < 100 ? FOCUS.SMALL : FOCUS.BIG,
  };
  return radParameters;
}

type ParameterType = 'kV' | 'mAs' | 'mA' | 'ms';

type EventCallback = (
  evt: Event & { target: XRayGeneratorController; eventName: string } & any
) => void;

const GENERATOR_MODBUS_ID = 85;
const MODBUS_TIMEOUT = 100;

enum GeneratorPhase {
  PHASE_VEILLE = 1,
  PHASE_STBY,
  PHASE_PREPATIR,
  PHASE_TIR,
  PHASE_FINTIR,

  PHASE_WAIT_FIRSTCOMM = 0xf0,
  PHASE_ERROR_COMM_VETTECH,
}
GeneratorPhase;

function detailedGeneratorStatus(exceptionStatus: number) {
  return {
    PREP_TIR: !!(exceptionStatus & ExceptionStatusOffset.PREP_TIR),
    ERROR: !!(exceptionStatus & ExceptionStatusOffset.ERROR),
    WAITING_FOR_CONSTANTS: !!(exceptionStatus & ExceptionStatusOffset.WAITING_FOR_CONSTANTS),
    ERROR_SYSTEM_LOCKED: !!(exceptionStatus & ExceptionStatusOffset.ERROR_SYSTEM_LOCKED),
    COMMUNICATION_DEFAULT: !!(exceptionStatus & ExceptionStatusOffset.COMMUNICATION_DEFAULT),
    WAIT_CAPACITOR: !!(exceptionStatus & ExceptionStatusOffset.WAIT_CAPACITOR),
    BIP_READY: !!(exceptionStatus & ExceptionStatusOffset.BIP_READY),
    NEED_WAIT_TUBE: !!(exceptionStatus & ExceptionStatusOffset.NEED_WAIT_TUBE),
  };
}

function isFastExceptionStatus(exceptionStatus: number) {
  return !!(
    exceptionStatus &
    (ExceptionStatusOffset.ERROR_SYSTEM_LOCKED |
      ExceptionStatusOffset.COMMUNICATION_DEFAULT |
      ExceptionStatusOffset.ERROR |
      ExceptionStatusOffset.PREP_TIR |
      ExceptionStatusOffset.WAIT_CAPACITOR |
      ExceptionStatusOffset.BIP_READY)
  );
}

function computeStatusFromExceptionStatusToUnitStatusAndPhase(
  exceptionStatus: number,
  generatorPhase: number,
  drReady: boolean
) {
  let unitStatus: GeneratorUnitStatus;
  if (
    exceptionStatus &
    (ExceptionStatusOffset.ERROR_SYSTEM_LOCKED |
      ExceptionStatusOffset.COMMUNICATION_DEFAULT |
      ExceptionStatusOffset.ERROR)
  ) {
    if (
      exceptionStatus &
      (ExceptionStatusOffset.ERROR_SYSTEM_LOCKED | ExceptionStatusOffset.COMMUNICATION_DEFAULT)
    ) {
      // VTECH_Pret = false;
      // DR_Ready = false;
    }
    unitStatus = GeneratorUnitStatus.SYSTEM_ERROR;
  } else if (exceptionStatus & ExceptionStatusOffset.PREP_TIR) {
    unitStatus = GeneratorUnitStatus.XRAY_IN_PROGRESS;
  } else if (exceptionStatus & ExceptionStatusOffset.BIP_READY) {
    unitStatus = GeneratorUnitStatus.FIRST_TIME;
  } else if (exceptionStatus & ExceptionStatusOffset.WAIT_CAPACITOR) {
    unitStatus = GeneratorUnitStatus.WAIT_CAPACITOR;
  } else {
    switch (generatorPhase) {
      case GeneratorPhase.PHASE_VEILLE:
        unitStatus = GeneratorUnitStatus.SLEEPING_MODE;
        break;
      case GeneratorPhase.PHASE_STBY:
        unitStatus = GeneratorUnitStatus.STANDBY_MODE;
        break;
      case GeneratorPhase.PHASE_PREPATIR:
        unitStatus = GeneratorUnitStatus.FIRST_TIME;
        break;
      case GeneratorPhase.PHASE_TIR:
        unitStatus = GeneratorUnitStatus.XRAY_IN_PROGRESS;
        break;
      case GeneratorPhase.PHASE_FINTIR:
        unitStatus = GeneratorUnitStatus.END_OF_XRAY;
        break;
      case GeneratorPhase.PHASE_WAIT_FIRSTCOMM:
        unitStatus = GeneratorUnitStatus.WAIT_FIRST_COM;
        break;
      case GeneratorPhase.PHASE_ERROR_COMM_VETTECH:
        unitStatus = GeneratorUnitStatus.SYSTEM_ERROR;
        break;
    }
  }

  let internalReady = unitStatus === GeneratorUnitStatus.STANDBY_MODE;
  if (
    exceptionStatus &
    (ExceptionStatusOffset.WAITING_FOR_CONSTANTS | ExceptionStatusOffset.WAIT_CAPACITOR)
  ) {
    internalReady = false;
  }

  return {
    unitStatus,
    readyStatus: internalReady && drReady ? GeneratorReadyStatus.READY : GeneratorReadyStatus.BUSY,
  };
}

function checkNullRadResult(radResult: ReturnType<typeof convertResultTirToStruct>) {
  return radResult.kV < 10 && radResult.mA < 10 && radResult.ms === 0;
}

export default class ModbusXRayGeneratorController extends EventTarget {
  private drReady: boolean = false;
  private lastGeneratorStatus: {
    unitStatus: GeneratorUnitStatus;
    readyStatus: GeneratorReadyStatus;
  };
  private lastReportedErrorCode: number | undefined;
  private generatorType: 'HF200' | 'HF250' | 'HF300';
  private vTechVersion: { version: number; major: number; minor: number };
  private isFastResponseLoopActive: boolean = false;
  private isFastLoopStopRequested: boolean = false;
  private switchStateChangeInterval: NodeJS.Timeout;
  private lastSwitchState: number | undefined;
  private requestedOperatingMode: OperatingMode | undefined;
  private accumulatedErrors: Set<number> = new Set();
  private accumulatedWarnings: Set<number> = new Set();
  private lastSetRadParameters: RadParameters | undefined;

  constructor(private modbusPort: SequentialModbusRTU) {
    super();
    if (!stream()) return undefined as unknown as ModbusXRayGeneratorController;
    console.debug('Create ModbusXRayGeneratorController', this.modbusPort);
    this.modbusPort.setID(GENERATOR_MODBUS_ID);
    this.modbusPort.setTimeout(MODBUS_TIMEOUT);
  }

  /** Provide a stream close function */
  close = (): void => {
    this.switchStateChangeInterval && clearInterval(this.switchStateChangeInterval);
    this.modbusPort.close();
  };

  /** Get accumulated errors and warnings since last FIRST_TIME state, then clear them */
  getAccumulatedErrors = (): { errorCodes: number[]; warningCodes: number[] } => {
    const result = {
      errorCodes: [...this.accumulatedErrors],
      warningCodes: [...this.accumulatedWarnings],
    };
    this.accumulatedErrors.clear();
    this.accumulatedWarnings.clear();
    return result;
  };

  /** Synchronise generator date */
  init = async () => {
    console.debug('Init generator controller', this.modbusPort);
    const { buffer: versionBuffer } = await this.modbusPort.readHoldingRegisters(
      MODBUS_ADDRESS.ADRESSE_MODBUS_VERSION,
      2
    );
    let verifSoftwareLock = (
      await this.modbusPort.readHoldingRegisters(
        MODBUS_ADDRESS.ADRESSE_MODBUS_VERIF_DEVEROUILLE_LOG,
        1
      )
    ).data[0];
    console.debug('verifSoftwareLock', verifSoftwareLock.toString(16), 'expected 0x5362');
    await TimeoutPromise(100);
    const idLogiciel = (
      await this.modbusPort.readHoldingRegisters(
        MODBUS_ADDRESS.ADRESSE_MODBUS_ID_LOGICIEL_AUTORISE,
        1
      )
    ).data[0];
    console.debug('idLogiciel', idLogiciel.toString(16));
    await TimeoutPromise(100);
    await this.modbusPort.writeRegisters(MODBUS_ADDRESS.ADRESSE_MODBUS_DEVEROUILLE_LOG, [
      idLogiciel,
    ]);
    await TimeoutPromise(100);
    verifSoftwareLock = (
      await this.modbusPort.readHoldingRegisters(
        MODBUS_ADDRESS.ADRESSE_MODBUS_VERIF_DEVEROUILLE_LOG,
        1
      )
    ).data[0];
    console.debug('second verifSoftwareLock', verifSoftwareLock.toString(16), 'expected 0x5362');

    const { status } = await this.modbusPort.readExceptionStatus();
    console.debug('exceptionStatus', detailedGeneratorStatus(status));

    const uint8Array = new Uint8Array(versionBuffer);
    const version = uint8Array[0] - '0'.charCodeAt(0);
    const major = uint8Array[1] - '0'.charCodeAt(0);
    const minor = uint8Array[2] - '0'.charCodeAt(0);
    this.vTechVersion = { version, major, minor };
    console.info('Version buffer:', versionBuffer);
    console.info('vTechVersion:', this.vTechVersion);
    const { buffer: deviceBuffer } = await this.modbusPort.readHoldingRegisters(
      MODBUS_ADDRESS.ADRESSE_MODBUS_QUEL_APPAREIL,
      3
    );
    this.generatorType = deviceBuffer.toString().slice(0, 5) as typeof this.generatorType;
    console.info('Device type buffer:', deviceBuffer, this.generatorType);

    const event = new Event(EVENT_SW_VER);
    Object.assign(event, { eventName: EVENT_SW_VER, ...this.vTechVersion });
    this.dispatchEvent(event);
    this.requestStatus();
  };

  /** Keep alive */
  requestStatus = async () => {
    // We want to avoid overlapping events and requests during the fast loop since it is an
    // altered operation mode where we want high availability of the generator.
    // fastLoop will automatically request status so we don't need to do it here and risk overlapping requests.
    if (this.isFastResponseLoopActive) return;

    try {
      const { generatorStatus, exceptionStatus } = await this.getStatus();

      const event = new Event(EVENT_GENERATOR_STATUS);
      Object.assign(event, {
        eventName: EVENT_GENERATOR_STATUS,
        ...generatorStatus,
      });
      console.debug('[ModbusXRayGeneratorController] Emitting EVENT_GENERATOR_STATUS', {
        ...generatorStatus,
      });

      await this.handleStatus(generatorStatus, exceptionStatus);

      this.dispatchEvent(event);
    } catch (error) {
      console.error('Error reading generator status', error);
    }
  };

  getStatus = async (priority: number = 0) => {
    let generatorPhase = 0;

    const { status: exceptionStatus } = await this.modbusPort.scheduleFunction(
      priority,
      this.modbusPort.modbusClient.readExceptionStatus.bind(
        this.modbusPort.modbusClient
      ) as typeof this.modbusPort.modbusClient.readExceptionStatus
    );
    const { data } = await this.modbusPort.scheduleFunction(
      priority,
      this.modbusPort.modbusClient.readHoldingRegisters.bind(
        this.modbusPort.modbusClient
      ) as typeof this.modbusPort.modbusClient.readHoldingRegisters,
      MODBUS_ADDRESS.ADRESSE_MODBUS_PHASE,
      1
    );

    generatorPhase = data[0];

    const generatorStatus = computeStatusFromExceptionStatusToUnitStatusAndPhase(
      exceptionStatus,
      generatorPhase,
      this.drReady
    );

    console.debug('Generator raw status', {
      exceptionStatus,
      detailedStatus: detailedGeneratorStatus(exceptionStatus),
      generatorPhase,
      generatorStatus,
    });

    return { generatorStatus, exceptionStatus, generatorPhase };
  };

  sendEndOfXRay = async () => {
    const exposureStateEvent = new Event(EVENT_GENERATOR_EXPOSURE_STATE);
    Object.assign(exposureStateEvent, { eventName: EVENT_GENERATOR_EXPOSURE_STATE, status: 0 });
    this.dispatchEvent(exposureStateEvent);

    const exposureParameters = convertResultTirToStruct(
      (await this.modbusPort.readInputRegisters(MODBUS_ADDRESS.ADRESSE_MODBUS_RESULTAT_TIR, 8)).data
    );
    if (checkNullRadResult(exposureParameters)) {
      console.warn(
        '[ModbusXRayGeneratorController] No valid shot results not sending exposure',
        exposureParameters
      );
      return;
    }
    const exposureParametersEvent = new Event(EVENT_GENERATOR_RAD_PARAMETERS_EXPOSURE);
    Object.assign(exposureParametersEvent, {
      eventName: EVENT_GENERATOR_RAD_PARAMETERS_EXPOSURE,
      ...exposureParameters,
    });
    console.log(
      '[ModbusXRayGeneratorController] EVENT_GENERATOR_RAD_PARAMETERS_EXPOSURE',
      exposureParameters
    );
    this.dispatchEvent(exposureParametersEvent);
  };

  emitGeneratorErrorIfNeeded = async (
    generatorStatus: { unitStatus: GeneratorUnitStatus; readyStatus: GeneratorReadyStatus },
    exceptionStatus: number
  ) => {
    const isError = !!(exceptionStatus & ExceptionStatusOffset.ERROR);
    const isErrorSystemLocked = !!(exceptionStatus & ExceptionStatusOffset.ERROR_SYSTEM_LOCKED);
    const isCommunicationDefault = !!(
      exceptionStatus & ExceptionStatusOffset.COMMUNICATION_DEFAULT
    );
    if (!isError && !isErrorSystemLocked && !isCommunicationDefault) {
      return;
    }
    const errorEventType = isErrorSystemLocked ? EVENT_GENERATOR_ERROR : EVENT_GENERATOR_WARNING;
    let errorCode = (
      await this.modbusPort.readInputRegisters(MODBUS_ADDRESS.ADRESSE_MODBUS_LISTE_ERROR, 1)
    ).data[0];
    let radResult;
    try {
      radResult = convertResultTirToStruct(
        (await this.modbusPort.readInputRegisters(MODBUS_ADDRESS.ADRESSE_MODBUS_RESULTAT_TIR, 8))
          .data
      );
    } catch {}
    const radParameters = this.lastSetRadParameters;

    errorCode = errorCode || radResult?.error || 0;
    if (this.lastReportedErrorCode === errorCode) {
      console.log(
        '[ModbusXRayGeneratorController] Same error code as last time, not emitting event',
        errorCode
      );
      return;
    }
    this.lastReportedErrorCode = errorCode;
    console.log('[ModbusXRayGeneratorController] Reporting error', errorCode, {
      radResult,
      radParameters,
    });

    if (checkNullRadResult(radResult)) {
      // Fully clearing radResult to exclude them from error event when they are not reliable (ms = 0 means no tir has been registered, so radResult is not relevant and can contain garbage values)
      // WE reset it after using the errorCode since it can contain the error without matching any exposure parameters.
      radResult = undefined;
    }

    if (errorCode === 0) {
      const event = new Event(errorEventType);
      Object.assign(event, {
        eventName: errorEventType,
        errorCode: 0x82,
        radParameters,
        radResult,
      });
      console.debug('[ModbusXRayGeneratorController] Emitting generator unknown error/warning ', {
        ...event,
      });
      this.dispatchEvent(event);
      return;
    }

    const errorInterpretation = convertErrorCodeToExamionCode(errorCode, isErrorSystemLocked);
    const eventType = errorEventType;
    const event = new Event(eventType);
    Object.assign(event, {
      eventName: eventType,
      ...errorInterpretation,
      radParameters,
      radResult,
    });
    console.debug('[ModbusXRayGeneratorController] Emitting generator error/warning event', {
      ...event,
    });
    this.dispatchEvent(event);
    if ('errorCode' in errorInterpretation) {
      this.accumulatedErrors.add(errorInterpretation.errorCode);
    } else {
      this.accumulatedWarnings.add(errorInterpretation.warningCode);
    }
  };

  handleStatus = async (
    generatorStatus: { unitStatus: GeneratorUnitStatus; readyStatus: GeneratorReadyStatus },
    exceptionStatus: number
  ) => {
    await this.emitGeneratorErrorIfNeeded(generatorStatus, exceptionStatus);

    if (generatorStatus.unitStatus !== GeneratorUnitStatus.SYSTEM_ERROR) {
      // We reset the error state when exiting SYSTEM_ERROR status to make sure that the same error can be reported again.
      this.lastReportedErrorCode = undefined;
    }
    if (generatorStatus.unitStatus !== this.lastGeneratorStatus?.unitStatus) {
      if (generatorStatus.unitStatus === GeneratorUnitStatus.FIRST_TIME) {
        // Clear accumulated errors when entering FIRST_TIME (prep) state
        this.accumulatedErrors.clear();
        this.accumulatedWarnings.clear();

        try {
          await this.modbusPort.writeRegisters(
            MODBUS_ADDRESS.ADRESSE_MODBUS_RESULTAT_TIR,
            [0, 0, 0, 0, 0, 0, 0, 0]
          );
        } catch {}
        try {
          this.lastSetRadParameters = convertRadParamsToStruct(
            (await this.modbusPort.readInputRegisters(MODBUS_ADDRESS.ADRESSE_MODBUS_PARAM_TIR, 4))
              .data
          );
          const event = new Event(EVENT_GENERATOR_RAD_PARAMETERS_CHANGE);
          Object.assign(event, {
            eventName: EVENT_GENERATOR_RAD_PARAMETERS_CHANGE,
            ...this.lastSetRadParameters,
          });
          this.dispatchEvent(event);
        } catch {}

        const event = new Event(EVENT_GENERATOR_PREP_STATE);
        Object.assign(event, { eventName: EVENT_GENERATOR_PREP_STATE, status: 1 });
        this.dispatchEvent(event);
        this.startXRayFastResponseLoop();
      }
    }
    this.lastGeneratorStatus = generatorStatus;
  };

  requestPedaleStateDetection = async (isEnabled: boolean) => {
    const isSwitchMonitorEnabled = !!this.switchStateChangeInterval;
    clearInterval(this.switchStateChangeInterval);
    this.lastSwitchState = undefined;

    if (!isEnabled) {
      if (this.requestedOperatingMode && isSwitchMonitorEnabled) {
        await this.changeOperatingMode(this.requestedOperatingMode);
      }
      return;
    }

    this.switchStateChangeInterval = setInterval(this.checkSwitchStateChange, 200);
    await this.modbusPort.writeRegister(MODBUS_ADDRESS.ADRESSE_MODBUS_RAD_ALLOWED, 0);
    await this.changeOperatingMode(OperatingMode.STANDBY);
    await this.modbusPort.writeRegisters(MODBUS_ADDRESS.ADRESSE_MODBUS_PARAM_TIR, [0x10, 0, 0, 0]);
  };

  private checkSwitchStateChange = async () => {
    try {
      const [phase] = (
        await this.modbusPort.modbusClient.readHoldingRegisters(
          MODBUS_ADDRESS.ADRESSE_MODBUS_PHASE,
          1
        )
      ).data;

      // We have to clear error to be able to read the switch state.
      const { ERROR } = detailedGeneratorStatus(
        (await this.modbusPort.readExceptionStatus()).status
      );
      if (ERROR) {
        await this.resetWarning();
      }

      if (phase !== OperatingMode.STANDBY) {
        await this.modbusPort.writeRegister(
          MODBUS_ADDRESS.ADRESSE_MODBUS_PHASE,
          OperatingMode.STANDBY
        );
      }
      const [etatPedale] = (
        await this.modbusPort.readHoldingRegisters(MODBUS_ADDRESS.ADRESSE_MODBUS_ETAT_SWITCH, 1)
      ).data;

      if (etatPedale === this.lastSwitchState) {
        return;
      }
      console.log('[ModbusXRayGeneratorController] Switch state changed', { etatPedale });
      const switchEvent = new Event(EVENT_GENERATOR_SWITCH);
      Object.assign(switchEvent, { eventName: EVENT_GENERATOR_SWITCH, status: etatPedale });
      this.lastSwitchState = etatPedale;
      this.dispatchEvent(switchEvent);
    } catch (error) {
      console.warn('[ModbusXRayGeneratorController] Error reading switch state', error);
    }
  };

  private startXRayFastResponseLoop() {
    console.log(
      '[ModbusXRayGeneratorController] Start fast response loop',
      this.isFastResponseLoopActive ? 'already active' : ''
    );
    if (this.isFastResponseLoopActive) return;

    this.isFastResponseLoopActive = true;
    this.isFastLoopStopRequested = false;
    const loop = async () => {
      let shotFired = false;
      while (this.isFastResponseLoopActive) {
        try {
          const { generatorStatus, exceptionStatus } = await this.getStatus(1);

          const status = detailedGeneratorStatus(exceptionStatus);

          if (status.PREP_TIR && !shotFired) {
            const event = new Event(EVENT_GENERATOR_EXPOSURE_STATE);
            const [etatPedale, prepRequested, tirRequested] = (
              await this.modbusPort.readHoldingRegisters(
                MODBUS_ADDRESS.ADRESSE_MODBUS_ETAT_SWITCH,
                3
              )
            ).data;
            console.log('[ModbusXRayGeneratorController] Sending EVENT_GENERATOR_EXPOSURE_STATE', {
              etatPedale,
              prepRequested,
              tirRequested,
            });
            Object.assign(event, { eventName: EVENT_GENERATOR_EXPOSURE_STATE, status: etatPedale });
            await this.dispatchEvent(event);
            shotFired = true;
          } else if (
            generatorStatus.unitStatus !== GeneratorUnitStatus.FIRST_TIME &&
            generatorStatus.unitStatus !== GeneratorUnitStatus.XRAY_IN_PROGRESS &&
            !status.PREP_TIR
          ) {
            this.isFastLoopStopRequested = true;
            console.log(
              '[ModbusXRayGeneratorController] Fast loop ended with status',
              getEnumKeyByValue(GeneratorUnitStatus, generatorStatus.unitStatus)
            );
            if (shotFired) {
              // Send end of X-Ray here because we might miss the real end of X-Ray status
              this.sendEndOfXRay();
            }
            break;
          } else if (
            status.ERROR ||
            status.WAIT_CAPACITOR ||
            status.COMMUNICATION_DEFAULT ||
            status.ERROR_SYSTEM_LOCKED ||
            status.NEED_WAIT_TUBE ||
            status.WAITING_FOR_CONSTANTS
          ) {
            console.log('[ModbusXRayGeneratorController] Fast loop ended with error', status);
            // In any error case, we exit the fast loop and go back to normal status polling
            break;
          }

          // We need to produce events in order for the system to not go into disconnected state.
          const statusEvent = new Event(EVENT_GENERATOR_STATUS);
          Object.assign(statusEvent, {
            eventName: EVENT_GENERATOR_STATUS,
            ...generatorStatus,
          });

          await this.handleStatus(generatorStatus, exceptionStatus);
          console.debug('[ModbusXRayGeneratorController] Emitting EVENT_GENERATOR_STATUS', {
            ...generatorStatus,
          });
          this.dispatchEvent(statusEvent);

          // BIP Ready, we are ready to fire at any time
          const waitTime = !status.BIP_READY ? 100 : 20;

          await TimeoutPromise(waitTime);
        } catch (error) {
          console.warn('[ModbusXRayGeneratorController] Fast loop read error', error);
        }

        if (this.isFastLoopStopRequested) break;
      }
      console.log('[ModbusXRayGeneratorController] End of fast loop');
      this.isFastResponseLoopActive = false;

      const event = new Event(EVENT_GENERATOR_PREP_STATE);
      Object.assign(event, { eventName: EVENT_GENERATOR_PREP_STATE, status: 0 });
      this.dispatchEvent(event);

      this.requestStatus();
    };

    loop();
  }

  stopXRayFastResponseLoop = () => {
    this.isFastLoopStopRequested = true;
  };

  /** Lock/unlock generator */
  lock = async (isLocked: boolean) => {
    if (this.drReady === !isLocked) return;
    this.drReady = !isLocked;

    console.debug(
      '[ModbusXRayGeneratorController] Unlocking generator useless for modbus protocol'
    );

    if (!this.drReady) {
      console.debug(
        '[ModbusXRayGeneratorController] Locking generator useless for modbus protocol'
      );

      // let focusType = 0;
      // try {
      //   const mAValue = (
      //     await this.modbusPort.readInputRegisters(MODBUS_ADDRESS.ADRESSE_MODBUS_PARAM_TIR + 2, 1)
      //   ).data[0];
      //   console.debug('[ModbusXRayGeneratorController] Current setmA', mAValue);
      //   focusType = mAValue > 75 ? 1 : 0;
      // } catch (error) {
      //   console.warn(
      //     '[ModbusXRayGeneratorController] Error reading type_foyer or setmA, using default values',
      //     error
      //   );
      // }
      await this.modbusPort.writeRegister(MODBUS_ADDRESS.ADRESSE_MODBUS_PREP_CONFIRM, 0);
      // await this.modbusPort.writeRegisters(
      //   MODBUS_ADDRESS.ADRESSE_MODBUS_PARAM_TIR,
      //   [0x10, 0, 0, 0]
      // );
      // if (focusType === FOCUS.SMALL) {
      //   await this.modbusPort.writeCoil(MODBUS_ADDRESS.ADRESSE_MODBUS_TYPE_FOYER, false);
      // }
    }
  };

  /** Change operating mode */
  changeOperatingMode = async (mode: OperatingMode) => {
    console.debug(
      '[ModbusXRayGeneratorController] Change operating mode to',
      getEnumKeyByValue(OperatingMode, mode)
    );
    this.requestedOperatingMode = mode;
    switch (mode) {
      case OperatingMode.SLEEPING:
        await this.modbusPort.writeRegister(
          MODBUS_ADDRESS.ADRESSE_MODBUS_PHASE,
          OperatingMode.SLEEPING
        );

        // resetWarning will request status
        await this.resetWarning();
        // Reset last set rad parameters since going back to SLEEPING require setting them again.
        this.lastSetRadParameters = undefined;
        break;
      case OperatingMode.STANDBY:
        await this.modbusPort.writeRegister(
          MODBUS_ADDRESS.ADRESSE_MODBUS_PHASE,
          OperatingMode.STANDBY
        );
        await this.modbusPort.writeRegister(MODBUS_ADDRESS.ADRESSE_MODBUS_PREP_CONFIRM, 0);
        await this.modbusPort.writeRegisters(MODBUS_ADDRESS.ADRESSE_MODBUS_PEDALE_STATE, [0, 0, 0]);
        await this.requestStatus();
        break;
      case OperatingMode.SHUTDOWN:
        // Specific action, we need more info
        await this.modbusPort.writeCoil(MODBUS_ADDRESS.ADRESSE_MODBUS_SHUTDOWN, true);
        break;
      default:
        throw new Error(`changeOperatingMode used with invalid mode '${mode}'`);
    }
  };

  /** Set complete radiological parameters */
  setRadParameters = async (requestedConstants: RadParameters) => {
    const adjustedConstants = computeClosestCompatibleGeneratorConstants(this.generatorType, {
      ...requestedConstants,
      s: requestedConstants.ms / 1000,
    });
    const adjustedConstantsWithMS = {
      ...adjustedConstants,
      ms: Math.floor(adjustedConstants.s * 1000),
    };

    if (
      !_.isEqual(
        _.pick(requestedConstants, ['kV', 'mA', 'ms']),
        _.pick(adjustedConstantsWithMS, ['kV', 'mA', 'ms'])
      )
    ) {
      console.log(
        `[ModbusXRayGeneratorController] Requested constants were adjusted : ${JSON.stringify(
          requestedConstants
        )} => ${JSON.stringify(adjustedConstantsWithMS)}`
      );
    }
    await this.modbusPort.writeRegisters(MODBUS_ADDRESS.ADRESSE_MODBUS_PARAM_TIR, [
      0x10,
      adjustedConstantsWithMS.kV,
      adjustedConstantsWithMS.mA,
      adjustedConstantsWithMS.ms,
    ]);

    const radParameters = convertRadParamsToStruct(
      (await this.modbusPort.readInputRegisters(MODBUS_ADDRESS.ADRESSE_MODBUS_PARAM_TIR, 4)).data
    );

    console.log(
      '[ModbusXRayGeneratorController] Radiological requested parameters',
      adjustedConstantsWithMS,
      'set parameters',
      radParameters
    );

    const event = new Event(EVENT_GENERATOR_RAD_PARAMETERS_CHANGE);
    Object.assign(event, {
      eventName: EVENT_GENERATOR_RAD_PARAMETERS_CHANGE,
      ...radParameters,
    });
    this.dispatchEvent(event);

    this.requestStatus();
  };

  /** Update single radiological parameter */
  updateRadParameter = (parameterType: ParameterType, shouldIncrement: boolean): void => {
    throw new Error('Not implemented yet');
  };

  /** Prep confirmation */
  prepConfirmation = async (allowed: boolean) => {
    if (!this.drReady || !allowed) {
      console.debug('[ModbusXRayGeneratorController] Prep confirmation denied', {
        drReady: this.drReady,
        allowed,
      });
      await this.modbusPort.writeRegister(
        MODBUS_ADDRESS.ADRESSE_MODBUS_PHASE,
        OPERATING_MODE.SLEEPING
      );
      await this.modbusPort.writeRegister(
        MODBUS_ADDRESS.ADRESSE_MODBUS_PHASE,
        OPERATING_MODE.STANDBY
      );
      await this.modbusPort.writeRegister(MODBUS_ADDRESS.ADRESSE_MODBUS_RAD_ALLOWED, 0);
      await this.modbusPort.writeRegister(MODBUS_ADDRESS.ADRESSE_MODBUS_PREP_CONFIRM, 0);
      await this.modbusPort.writeRegisters(MODBUS_ADDRESS.ADRESSE_MODBUS_PEDALE_STATE, [0, 0, 0]);
    } else {
      console.debug('[ModbusXRayGeneratorController] Prep confirmation accepted', {
        drReady: this.drReady,
        allowed,
      });
      await this.modbusPort.writeRegister(MODBUS_ADDRESS.ADRESSE_MODBUS_PREP_CONFIRM, 1);
    }

    await this.requestStatus();
  };

  /** Cancel preparation */
  cancelPreparation = (): void => {
    this.changeOperatingMode((OPERATING_MODE as any).STANDBY);
  };

  /** Start X-Ray exposure */
  startXRay = async (allowed: boolean) => {
    console.log(
      '[ModbusXRayGeneratorController] X-Ray',
      this.drReady && allowed ? 'start' : 'end',
      'requested'
    );
    try {
      await this.modbusPort.writeRegister(
        MODBUS_ADDRESS.ADRESSE_MODBUS_RAD_ALLOWED,
        this.drReady && allowed ? 1 : 0
      );
    } catch (error) {
      console.error('Error writing ADRESSE_MODBUS_RAD_ALLOWED', error);
    }
    await TimeoutPromise(100);
    await this.requestStatus();
  };

  /** Request thickness */
  requestThickness = (): void => {
    this.modbusPort
      .readHoldingRegisters(MODBUS_ADDRESS.ADRESSE_MODBUS_EPAISSEUR, 1)
      .then(({ data }) => {
        const event = new Event(EVENT_GENERATOR_THICKNESS);
        const thickness = data[0];
        Object.assign(event, { eventName: EVENT_GENERATOR_THICKNESS, thickness });
        this.dispatchEvent(event);
      });
  };

  /** Reset warning */
  resetWarning = async () => {
    console.debug('[ModbusXRayGeneratorController] Reset generator warning');
    await this.modbusPort.writeCoil(MODBUS_ADDRESS.ADRESSE_MODBUS_BIT_CLEAR_ERROR, true);
    this.lastReportedErrorCode = undefined;
    // We block the modbus port for a short time to avoid reading old error state before it is cleared in the generator.
    await this.modbusPort.scheduleFunction(1, async () => {
      await TimeoutPromise(150);
    });
    await this.requestStatus();
    // await this.changeOperatingMode(OPERATING_MODE.STANDBY);
  };

  getRemainingCooldownTime = async () =>
    this.modbusPort
      .readHoldingRegisters(MODBUS_ADDRESS.ADRESSE_MODBUS_TIMER_TUBE, 1)
      .then(({ data }) => {
        const cooldownTime = data[0];
        // The generator returns 65520 when the cooldown is ready, we consider that as no cooldown needed
        if (cooldownTime > 65000) return 0;
        return Math.ceil(cooldownTime / 1000);
      });

  /** Shutdown */
  shutdown = (): void => {
    this.changeOperatingMode(OPERATING_MODE.SLEEPING);
  };

  on = (eventName: string, callback: EventCallback): void =>
    this.addEventListener(eventName, callback as EventListener);
}
